import { yupResolver } from '@hookform/resolvers/yup'
import { FormLabel, Grid } from '@mui/material'
import axios from 'axios'
import React, { Fragment, useContext, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { format } from 'date-fns'
import { AuthContext } from '../../../context/AuthProvider'
import FormStepper from '../../../components/FormStepper'
import {
  ErrorMessage,
  LaakariButton,
  ResultsP,
  ShadowContainer,
  SubHeaderH3
} from '../../../components/styled/uiComponents'
import { MetadataContext, UserContext } from '../../../context'
import { CreateJobFormData } from '../../../types/job'
import { isSessionInvalidError } from '../../../utils/error'
import {
  buildApiUrl,
  parseEmploymentValues,
  parseMembershipEmploymentFormData,
  parseNewJobFormData
} from '../../../utils/utils'
import EmploymentForm from '../../Forms/MembershipForm/EmploymentForm'

interface JobSummaryProps {
  formState: any
}
const formatDate = (val: string): any => {
  if (val) {
    return format(new Date(val), 'dd.MM.yyyy')
  }
  return val
}
const JobSummary: React.FC<JobSummaryProps> = ({ formState }) => {
  const employmentValues = formState.employment
    ? parseEmploymentValues(formState.employment)
    : undefined
  return (
    <Fragment>
      <SubHeaderH3>Työpaikkatiedot</SubHeaderH3>
      <div>
        <FormLabel>Työnantajasektori</FormLabel>
        <ResultsP>{JSON.parse(formState.employment.els_tyosuhdesektori)?.value}</ResultsP>
      </div>
      <div>
        <FormLabel>Työpaikka</FormLabel>
        <ResultsP>{JSON.parse(formState.employment.els_soteorganisaatio)?.els_longname}</ResultsP>
        {/*//els_job*/}
      </div>
      <div>
        <FormLabel>Toimiala</FormLabel>
        <ResultsP>{JSON.parse(formState.employment.tyosuhdetoimiala)?.value}</ResultsP>
      </div>
      <div>
        <FormLabel>Työpaikan sijainti</FormLabel>
        <ResultsP>
          {JSON.parse(formState.employment.sll_tyosuhde_sijaintikunta)?.els_koodinnimi}
        </ResultsP>
      </div>
      <div>
        <FormLabel>Ammattinimike</FormLabel>
        <ResultsP>{employmentValues.ammattinimike?.els_koodinnimi}</ResultsP>
      </div>
      <div>
        <FormLabel>Onko tämä päätoiminen työpaikkasi?</FormLabel>
        <ResultsP>{formState.employment.els_paatoimi === 'yes' ? 'Kyllä' : 'Ei'}</ResultsP>
      </div>
      <div>
        <FormLabel>Työajan jakautuminenx</FormLabel>
        <ResultsP>
          {formState.employment.sll_tyoaika === '921950000' ? 'Kokoaikainen ' : 'Osa-aikainen'}
        </ResultsP>
      </div>
      <div>
        <FormLabel>Työsuhteen luonne</FormLabel>
        <ResultsP>
          {formState.employment.tyosuhdeluonne === 'maaraaikainen'
            ? 'Määräaikainen'
            : 'Toistaiseksi voimassa oleva'}
        </ResultsP>
      </div>
      <div>
        <FormLabel>Aloituspäivämäärä</FormLabel>
        <ResultsP>{formatDate(formState.employment.els_alkupvm)}</ResultsP>
      </div>
      {formState.employment.tyosuhdeluonne === 'maaraaikainen' && (
        <div>
          <FormLabel>Päättymispäivämäärä</FormLabel>
          <ResultsP>{formatDate(formState.employment.els_loppupvm)}</ResultsP>
        </div>
      )}
    </Fragment>
  )
}

interface Props {
  closeDialog: () => void
}

const CreateJobForm: React.FC<Props> = ({ closeDialog }) => {
  const [page, setPage] = useState(0)
  const { user } = useContext(UserContext)
  const { metaData } = useContext(MetadataContext)
  const { bearerToken, evaluateToken, handleLogout } = useContext(AuthContext)
  const [submitError, setSubmitError] = useState(false)

  const navigate = useNavigate()

  const employment1Schema = yup.object().shape({
    employment: yup
      .object()
      .shape({
        tyosuhdetoimiala: yup.string().required('Valitse toimiala'),
        els_tyosuhdesektori: yup.string().required('Valitse sektori'),
        els_soteorganisaatio: yup.string().required('Valitse työpaikka'), //els_job
        sll_tyosuhde_sijaintikunta: yup.string().required('Valitse työpaikan sijainti'),
        ammattinimike: yup.string().required('Valitse ammattinimike'),
        els_alkupvm: yup.string().required('Työsuhde alkaa on pakollinen'),
        els_loppupvm: yup
          .string()
          .nullable()
          .when('tyosuhdeluonne', {
            is: 'maaraaikainen',
            then: schema => schema.required('Määräaikaisen työsuhteen loppupäivä on pakollinen')
          })
          .test(
            'end-after-start',
            'Työsuhteen loppupäivän tulee olla alkupäivän jälkeen',
            function (end) {
              const start = this.parent?.els_alkupvm
              if (!start || !end) {
                return true
              }
              const re = /^\d{4}-\d{2}-\d{2}$/
              if (!re.test(start) || !re.test(end)) {
                return true
              }
              return end > start
            }
          )
      })
      .required()
  })

  /** Empty schema since Summary page doesn't have any fields */
  const summarySchema = yup.object().shape({})

  const createJobSchema = [employment1Schema, summarySchema]

  const currentValidationSchema = createJobSchema[page]

  const {
    register,
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm<CreateJobFormData>({
    shouldUnregister: false,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(currentValidationSchema)
  })

  // Re-validate els_loppupvm when els_alkupvm changes
  const elsAlkupvm = watch('employment.els_alkupvm')
  useEffect(() => {
    // Only trigger if alkupvm has a valid date format
    if (elsAlkupvm && /^\d{4}-\d{2}-\d{2}$/.test(elsAlkupvm)) {
      trigger('employment.els_loppupvm')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elsAlkupvm])

  const handleNext = async () => {
    const isStepValid = await trigger()
    if (isStepValid) {
      setPage(prevActivePage => prevActivePage + 1)
    }
  }

  const handleBack = () => {
    setPage(prevActivePage => prevActivePage - 1)
    if (submitError) {
      setSubmitError(false)
    }
  }

  const onSubmit = async (data: any) => {
    try {
      const employment = parseNewJobFormData(data)
      const finalData = !employment.els_loppupvm
        ? (() => {
            const { els_loppupvm, ...dataWithoutEndDate } = employment
            return dataWithoutEndDate
          })()
        : employment
      const url = buildApiUrl('employment')
      await axios.post(url, finalData, {
        headers: { 'x-tietoni-token': `Bearer ${bearerToken}` }
      })
      evaluateToken()
      closeDialog()
    } catch (error) {
      setSubmitError(true)
      console.log('error when submitting form', error)
      if (isSessionInvalidError(error)) {
        closeDialog()
        toast.error('Istunto on päättynyt. Kirjaudu uudelleen sisään.')
        handleLogout(() => navigate('/'))
      }
    }
  }

  const pageProps = { register, user, errors, metaData, getValues, control, setValue }
  return (
    <Fragment>
      <FormStepper activeStep={page} stepsCount={2} />
      {metaData && (
        <Fragment>
          <form>
            <ShadowContainer>
              {page === 0 && <EmploymentForm watch={watch} {...pageProps} />}
              {page === 1 && <JobSummary formState={getValues()} />}
            </ShadowContainer>

            <Grid
              container
              spacing={3}
              direction='row'
              wrap='wrap-reverse'
              alignItems='center'
              justifyContent='space-between'
              sx={{ paddingTop: '3vh' }}>
              {page === 0 && (
                <Grid item xs={12} md={4}>
                  <LaakariButton variant='outlined' onClick={closeDialog}>
                    Takaisin
                  </LaakariButton>
                </Grid>
              )}
              {page !== 0 && (
                <Grid item xs={12} md={4}>
                  <LaakariButton variant='outlined' onClick={handleBack}>
                    Takaisin
                  </LaakariButton>
                </Grid>
              )}
              {page !== 1 ? (
                <Grid item xs={12} md={4}>
                  <LaakariButton onClick={handleNext} disabled={Object.keys(errors).length > 0}>
                    Jatka
                  </LaakariButton>
                </Grid>
              ) : (
                <Grid item xs={12} md={4}>
                  <LaakariButton disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
                    Lähetä
                  </LaakariButton>
                </Grid>
              )}
            </Grid>
          </form>
        </Fragment>
      )}
      {submitError && (
        <ErrorMessage>Jotain meni pieleen lisätessä työpaikkaa + {submitError}</ErrorMessage>
      )}
    </Fragment>
  )
}

export default CreateJobForm
